import React from "react";
import { NavLink, Link } from "react-router-dom";
import pipaanlogo from "../../assets/pipaanlogo.png";
import { ACCENT } from "./shared";
import TermsReacceptGate from "./TermsReacceptGate";

const ICONS = {
  dashboard: "M4 20V10M10 20V4M16 20v-8M22 20H2",
  profile: "M20 21a8 8 0 1 0-16 0M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
  catalogue: "M9 18V5l12-2v13M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0zM21 16a3 3 0 1 1-6 0 3 3 0 0 1 6 0z",
  money: "M12 2v20M17 6H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6",
  wallet: "M3 7h18v12H3zM3 10h18M16 15h2",
  awards: "M12 14a6 6 0 1 0 0-12 6 6 0 0 0 0 12zM8.5 13.5 7 22l5-3 5 3-1.5-8.5",
  heart: "M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8z",
  shield: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z",
  verify: "M9 12l2 2 4-4M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z",
  back: "M15 18l-6-6 6-6",
  globe: "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM2 12h20M12 2a15 15 0 0 1 0 20 15 15 0 0 1 0-20z",
};

function Icon({ name }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={ICONS[name]} />
    </svg>
  );
}

// The sidebar follows the design: Verification and Public Page are reachable by their URLs but are not listed here. Only Artist Profile, Dashboard, Catalogue, Monetization, Music Wallet, Awards, Fan Funding and Rights & Legal exist so far; the rest are shown but
// not yet available, so they are disabled instead of linking to a dead page.
const ITEMS = [
  { key: "dashboard", label: "Dashboard", icon: "dashboard", to: "/artist-studio/dashboard" },
  { key: "profile", label: "Artist Profile", icon: "profile", to: "/artist-studio/profile" },
  { key: "tracks", label: "Catalogue", icon: "catalogue", to: "/artist-studio/tracks" },
  { key: "monetization", label: "Monetization", icon: "money", to: "/artist-studio/monetization" },
  { key: "wallet", label: "Music Wallet", icon: "wallet", to: "/artist-studio/wallet" },
  { key: "awards", label: "Awards", icon: "awards", to: "/artist-studio/awards" },
  { key: "funding", label: "Fan Funding", icon: "heart", to: "/artist-studio/fan-funding" },
  { key: "rights", label: "Rights & Legal", icon: "shield", to: "/artist-studio/rights" },
];

const itemClass = (active, disabled) =>
  `flex items-center gap-3 rounded-full px-5 py-3 text-[14px] font-semibold transition whitespace-nowrap ${
    disabled
      ? "bg-white text-gray-400 cursor-not-allowed"
      : active
      ? "text-white"
      : "bg-white text-gray-900 hover:bg-gray-100"
  }`;

function userInfo() {
  try {
    const u = JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null");
    return {
      name: [u?.first_name, u?.last_name].filter(Boolean).join(" ") || u?.name || u?.username || "",
      handle: u?.username ? `@${u.username}` : "",
      image: u?.profile_picture_url || u?.profile_image || u?.profile_picture
        || sessionStorage.getItem("profilePictureUrl") || localStorage.getItem("profilePictureUrl") || "",
    };
  } catch (e) {
    return { name: "", handle: "", image: "" };
  }
}

// Artist Studio chrome shown to verified artists: studio sidebar + page area.
// `active` is the key of the highlighted item (see ITEMS).
export default function StudioShell({ active, children }) {
  const user = userInfo();
  return (
    <div className="min-h-screen w-full flex flex-col md:flex-row bg-white">
      <aside className="md:w-72 md:shrink-0 bg-[#f6f4f2] md:min-h-screen px-4 py-5 md:px-6 md:py-8 flex flex-col">
        <div className="mb-4 md:mb-8 px-1">
          <img src={pipaanlogo} alt="Nyawoo" className="h-8 w-auto" />
          <p className="text-[11px] font-bold tracking-widest mt-1" style={{ color: ACCENT }}>ARTIST STUDIO</p>
        </div>
        <nav className="flex md:flex-col gap-2 overflow-x-auto md:overflow-visible pb-2 md:pb-0" aria-label="Artist Studio">
          {ITEMS.map((item) => {
            const Inner = (
              <>
                <Icon name={item.icon} />
                <span>{item.label}</span>
                {!item.to && <span className="ml-auto text-[10px] uppercase tracking-wide">Soon</span>}
              </>
            );
            if (!item.to) {
              return (
                <span key={item.key} aria-disabled="true" className={itemClass(false, true)}>
                  {Inner}
                </span>
              );
            }
            const isActive = active === item.key;
            return (
              <NavLink
                key={item.key}
                to={item.to}
                className={itemClass(isActive, false)}
                style={isActive ? { backgroundColor: "#0b0f19" } : undefined}
                aria-current={isActive ? "page" : undefined}
              >
                {Inner}
              </NavLink>
            );
          })}
        </nav>
        <div className="hidden md:block mt-auto pt-8">
          <Link to="/home" className={itemClass(false, false)}>
            <Icon name="back" />
            <span>Exit Studio</span>
          </Link>
        </div>
      </aside>

      <main className="flex-1 min-w-0 px-4 py-6 md:px-10 md:py-8">
        <div className="flex items-center justify-end gap-3 mb-6">
          <div className="text-right leading-tight">
            <p className="font-semibold text-gray-900">{user.name}</p>
            <p className="text-sm text-gray-500">{user.handle}</p>
          </div>
          <div className="w-11 h-11 rounded-full bg-gray-200 overflow-hidden border border-amber-200 flex items-center justify-center text-sm font-bold text-gray-600">
            {user.image ? <img src={user.image} alt="" className="w-full h-full object-cover" /> : (user.name || "?").charAt(0).toUpperCase()}
          </div>
        </div>
        <TermsReacceptGate />
        {children}
        <div className="md:hidden mt-8">
          <Link to="/home" className="text-sm text-gray-500 underline">Exit Studio</Link>
        </div>
      </main>
    </div>
  );
}
